import { useEffect, useRef } from 'react';
import { DmnModel } from '../types';
import { createDmnAiCapability, EditorAiCapability } from './aiCapability';

/**
 * Wire the DMN editor's AI capability to the live model state and hand it to the host
 * via `onAiCapabilityReady`. A STABLE capability is built ONCE and reads/writes through
 * refs, so it never goes stale and `onAiCapabilityReady` is NOT called on every render.
 *
 * @param model      the current model (may be null while loading)
 * @param setModel   the editor's setState — the same path the UI uses, so changes apply live
 * @param onReady    optional host callback; receives the capability when ready, null on unmount
 */
export function useDmnAiCapability(
  model: DmnModel | null,
  setModel: (m: DmnModel) => void,
  onReady?: (cap: EditorAiCapability | null) => void,
): void {
  // Always-fresh handles so the stable capability never closes over stale values.
  const modelRef = useRef<DmnModel | null>(model);
  modelRef.current = model;

  const setModelRef = useRef(setModel);
  setModelRef.current = setModel;

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Build the capability exactly once.
  const capRef = useRef<EditorAiCapability | null>(null);
  if (capRef.current === null) {
    capRef.current = createDmnAiCapability({
      getModel: () => modelRef.current,
      setModel: (m) => setModelRef.current(m),
    });
  }

  // Publish on mount; retract on unmount. Empty deps → fires once (not per render).
  useEffect(() => {
    onReadyRef.current?.(capRef.current);
    return () => onReadyRef.current?.(null);
  }, []);
}
